import { Head, Link, router, usePage } from "@inertiajs/react"
import { AlertTriangle, Send01 } from "@untitledui/icons"
import { format } from "date-fns"
import { useState } from "react"

import { PageHeader } from "@/components/console/page-header"
import { Badge, StatusBadge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { AdminLayout } from "@/layouts/admin-layout"
import {
  adminEmailMessagePath,
  adminEmailMessagesPath,
  resendAdminEmailMessagePath,
} from "@/routes"

import {
  OUTCOME_COLOR as DRY_RUN_OUTCOME_COLOR,
  OUTCOME_LABEL as DRY_RUN_OUTCOME_LABEL,
} from "../health/types"

import {
  type EmailMessageSummary,
  type EmailStatus,
  type MessagePreview,
  OUTCOME_COLOR,
  OUTCOME_LABEL,
  STATUS_COLOR,
  STATUS_LABEL,
  type WebhookEvent,
} from "./types"

interface EmailMessage extends EmailMessageSummary {
  dedupe_key: string
  provider_message_id: string | null
  error: string | null
  resent_by: string | null
  sent_at: string | null
  delivered_at: string | null
  bounced_at: string | null
  complained_at: string | null
  failed_at: string | null
  suppressed_at: string | null
  causer: {
    type: string
    id: number
    label: string
    url: string | null
  } | null
}

interface TimelineEvent {
  id: string
  at: string
  kind: EmailStatus | "recorded"
  detail: string | null
}

interface Suppression {
  address: string
  reason: string
  suppressed_at: string
}

interface Props {
  message: EmailMessage
  timeline: TimelineEvent[]
  webhook_events: WebhookEvent[]
  suppressions: Suppression[]
  resends: EmailMessageSummary[]
  resend_of: EmailMessageSummary | null
  resend_preview: MessagePreview
}

const formatAt = (iso: string) =>
  format(new Date(iso), "d MMM yyyy 'at' HH:mm:ss")

export default function AdminEmailMessageShow({
  message,
  timeline,
  webhook_events,
  suppressions,
  resends,
  resend_of,
  resend_preview,
}: Props) {
  const { errors } = usePage().props
  const baseError = (
    errors as unknown as Record<string, string | undefined> | undefined
  )?.base

  // ResendMessage mints a fresh `dedupe_key` on purpose, so it opts out of the
  // ledger's double-send abort — which leaves this button as the only guard in
  // the path. Without it two clicks issue two deliver_later jobs, and for the
  // credential-bearing mailers that means two independently valid tokens in two
  // emails. The early return covers the second press landing in the same frame,
  // before React has re-rendered the disabled state.
  const [resending, setResending] = useState(false)

  const resend = () => {
    if (resending) return

    router.post(
      resendAdminEmailMessagePath(message.id),
      {},
      {
        preserveScroll: true,
        onStart: () => setResending(true),
        onFinish: () => setResending(false),
      },
    )
  }

  return (
    <AdminLayout>
      <Head title={`Email · ${message.to}`} />
      <div className="mx-auto flex max-w-4xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[
            { label: "email" },
            { label: "ledger", href: adminEmailMessagesPath() },
            { label: `#${message.id}` },
          ]}
          code={`#${message.id}`}
          title={message.subject ?? "(no subject)"}
          actions={
            <div className="flex flex-col items-start gap-1.5 md:max-w-sm md:items-end">
              <Button
                color="secondary"
                size="md"
                iconLeading={Send01}
                onPress={resend}
                isDisabled={resending}
                isLoading={resending}
                showTextWhileLoading
              >
                {resending ? "Resending…" : "Resend"}
              </Button>
              <p className="text-tertiary text-sm md:text-right">
                A resend now: {resend_preview.explanation}
              </p>
              {resend_preview.recipients.length > 1 && (
                <ul className="flex flex-col items-start gap-1 md:items-end">
                  {resend_preview.recipients.map((recipient) => (
                    <li
                      key={recipient.address}
                      className="flex items-center gap-2"
                    >
                      <span className="notation">{recipient.address}</span>
                      <StatusBadge
                        tier={DRY_RUN_OUTCOME_COLOR[recipient.outcome]}
                      >
                        {DRY_RUN_OUTCOME_LABEL[recipient.outcome]}
                      </StatusBadge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          }
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-tertiary text-sm">
              to <span className="notation">{message.to}</span>
            </span>
            <StatusBadge tier={STATUS_COLOR[message.status]}>
              {STATUS_LABEL[message.status]}
            </StatusBadge>
          </div>
        </PageHeader>

        {baseError && (
          <div className="caution-band text-caution-700 px-4 py-3 text-sm">
            {baseError}
          </div>
        )}

        {suppressions.length > 0 && (
          <div className="caution-band text-caution-700 flex items-start gap-3 px-4 py-3 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <div>
              {suppressions.map((suppression) => (
                <p key={suppression.address}>
                  <strong className="font-mono text-xs">
                    {suppression.address}
                  </strong>{" "}
                  is suppressed ({suppression.reason.replace("_", " ")} ·{" "}
                  <span className="font-mono text-xs tabular-nums">
                    {formatAt(suppression.suppressed_at)}
                  </span>
                  ) — new sends to it are aborted as undeliverable.
                </p>
              ))}
            </div>
          </div>
        )}

        <section className="border-secondary bg-primary rounded-xl border p-6">
          <h2 className="text-primary mb-4 text-lg font-semibold">Details</h2>
          <dl className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm md:grid-cols-2">
            <DetailRow label="Mailer">
              <Badge type="modern" color="gray" size="sm">
                {message.mailer}
              </Badge>
            </DetailRow>
            <DetailRow label="Cause">{message.cause ?? "—"}</DetailRow>
            <DetailRow label="Caused by">
              {message.causer ? (
                message.causer.url ? (
                  <Link
                    href={message.causer.url}
                    className="text-brand-secondary underline decoration-transparent transition-colors hover:decoration-current"
                  >
                    {message.causer.label}
                  </Link>
                ) : (
                  `${message.causer.label} (${message.causer.type})`
                )
              ) : (
                "—"
              )}
            </DetailRow>
            <DetailRow label="Route">{message.route}</DetailRow>
            <DetailRow label="Provider message id">
              <span className="font-mono text-xs break-all tabular-nums">
                {message.provider_message_id ?? "—"}
              </span>
            </DetailRow>
            <DetailRow label="Dedupe key">
              <span className="font-mono text-xs break-all tabular-nums">
                {message.dedupe_key}
              </span>
            </DetailRow>
            {message.error && (
              <DetailRow label="Error">{message.error}</DetailRow>
            )}
            {resend_of && (
              <DetailRow label="Resend of">
                <Link
                  href={adminEmailMessagePath(resend_of.id)}
                  className="text-brand-secondary hover:text-brand-secondary_hover font-mono text-xs tabular-nums"
                >
                  {`#${resend_of.id} (${formatAt(resend_of.created_at)})`}
                </Link>
              </DetailRow>
            )}
            {message.resent_by && (
              <DetailRow label="Resent by">{message.resent_by}</DetailRow>
            )}
          </dl>
        </section>

        <section className="border-secondary bg-primary rounded-xl border p-6">
          <h2 className="text-primary mb-4 text-lg font-semibold">Timeline</h2>
          <ol className="flex flex-col gap-0">
            {timeline.map((event, index) => (
              <li key={event.id} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <span
                    className={
                      event.kind === "recorded"
                        ? "bg-quaternary mt-1.5 size-2.5 rounded-full"
                        : "bg-brand-solid mt-1.5 size-2.5 rounded-full"
                    }
                  />
                  {index < timeline.length - 1 && (
                    <span className="bg-border-secondary w-px flex-1" />
                  )}
                </div>
                <div className="flex flex-col pb-6">
                  <p className="text-primary text-sm font-medium">
                    {event.kind === "recorded"
                      ? "Recorded"
                      : STATUS_LABEL[event.kind]}
                  </p>
                  <p className="text-quaternary font-mono text-xs tabular-nums">
                    {formatAt(event.at)}
                  </p>
                  {event.detail && (
                    <p className="text-tertiary mt-1 text-sm">{event.detail}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="border-secondary bg-primary rounded-xl border p-6">
          <h2 className="text-primary mb-1 text-lg font-semibold">
            Provider events
          </h2>
          <p className="text-tertiary mb-4 text-sm">
            What Postmark told us, as it arrived. The timeline above is
            projected from this row&rsquo;s own timestamps, so it shows what the
            message became — these are what said so.
          </p>
          {webhook_events.length === 0 ? (
            <p className="text-tertiary text-sm">
              {message.route === "sandbox"
                ? "None — sandbox-routed mail never receives webhooks."
                : "None received. A live row sitting at Sent with no events is usually the webhook endpoint, not the pipeline."}
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {webhook_events.map((event) => (
                <li
                  key={event.id}
                  className="border-secondary flex flex-col gap-1 border-b pb-3 last:border-b-0 last:pb-0"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge type="modern" color="gray" size="sm">
                      {event.record_type}
                    </Badge>
                    <StatusBadge tier={OUTCOME_COLOR[event.outcome]}>
                      {OUTCOME_LABEL[event.outcome]}
                    </StatusBadge>
                    <span className="notation">
                      {formatAt(event.received_at)}
                    </span>
                  </div>
                  {event.description && (
                    <p className="text-secondary text-sm">
                      {event.description}
                    </p>
                  )}
                  {event.outcome_detail && (
                    <p className="text-tertiary text-sm">
                      {event.outcome_detail}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {resends.length > 0 && (
          <section className="border-secondary bg-primary rounded-xl border p-6">
            <h2 className="text-primary mb-4 text-lg font-semibold">Resends</h2>
            <ul className="flex flex-col gap-2 text-sm">
              {resends.map((entry) => (
                <li key={entry.id} className="flex items-center gap-3">
                  <StatusBadge tier={STATUS_COLOR[entry.status]}>
                    {STATUS_LABEL[entry.status]}
                  </StatusBadge>
                  <Link
                    href={adminEmailMessagePath(entry.id)}
                    className="text-brand-secondary hover:text-brand-secondary_hover font-mono text-xs tabular-nums"
                  >
                    {`#${entry.id} · ${formatAt(entry.created_at)}`}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </AdminLayout>
  )
}

function DetailRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="eyebrow">{label}</dt>
      <dd className="text-secondary">{children}</dd>
    </div>
  )
}
